import React, { useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FilterTags } from 'src/components';
import { CircleIconFilled } from 'src/components/icons/CircleIconFilled';
import {
  getIsSigningStatusState,
  SIGNING_STATUS_STATES,
} from 'src/components/workflow/workflow-list/signing-status';
import type { SigningStatusState } from 'src/components/workflow/workflow-list/signing-status';
import { getWorkflowStateColor, getWorkflowStateDisplayString } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
}));

export function WorkflowSigningStatusAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<SigningStatusState[]>(
    getSigningStatusStates(item.value ?? [])
  );
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(states: SigningStatusState[]): void {
    applyValue({
      ...item,
      value: states,
    });
    setValue(states);
  }

  return (
    <Autocomplete<SigningStatusState, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => getWorkflowStateDisplayString(option)}
      inputValue={inputValue}
      options={[...SIGNING_STATUS_STATES]}
      renderInput={params => {
        return (
          <TextField placeholder="Search signing statuses..." {...params} variant="standard" />
        );
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        const color = getWorkflowStateColor(option);
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <CircleIconFilled className={classes.statusIcon} fill={color} />
            <Typography style={{ color }}>{getWorkflowStateDisplayString(option)}</Typography>
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<SigningStatusState>
            getTextFromTag={tag => getWorkflowStateDisplayString(tag)}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(filterTag => tag !== filterTag))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, value) => handleValueChange(value)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}

/** The grid filter value is untyped, so keep only the states this picker can display. */
function getSigningStatusStates(value: unknown): SigningStatusState[] {
  if (!Array.isArray(value)) return [];

  return value.filter(getIsSigningStatusState);
}
